import { useState, useEffect, useCallback } from "react";
import { Info, HelpCircle, BookOpen, Download, PenLine } from "lucide-react";
import SocialLinks from "@/components/SocialLinks";
import { useNavigate } from "react-router-dom";
import { Globe, Tv, Shield, RotateCcw, ChevronRight, LogIn, Bug, Smartphone, Trash2, AlertTriangle, Eraser, AtSign, Copy, Check, Camera, Languages, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { TMDB_LANGUAGE_MAP } from "@/i18n/config";
import { useAuth } from "@/contexts/AuthContext";
import GoPremiumModal from "@/components/GoPremiumModal";
import AvatarPicker from "@/components/AvatarPicker";
import UserAvatar from "@/components/UserAvatar";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { usePreferences } from "@/hooks/usePreferences";
import UsernameSetup from "@/components/UsernameSetup";
import { usePwaInstall } from "@/hooks/usePwaInstall";
import NativeAppWaitlistModal from "@/components/NativeAppWaitlistModal";
import RegionPicker from "@/components/RegionPicker";
import ProviderPicker from "@/components/ProviderPicker";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";


const Settings = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, isPremium, premiumType, subscriptionEnd, cancelAtPeriodEnd, isAdmin, username, avatarUrl, setPremiumOverride, checkSubscription, signOut, refreshProfile } = useAuth();
  const { setRegion: saveRegion, setProviders: saveProviders, setOnboarded } = usePreferences();
  const [showUsernameEdit, setShowUsernameEdit] = useState(false);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showDevTools, setShowDevTools] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [devices, setDevices] = useState<any[]>([]);
  const [showDevices, setShowDevices] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showResetChoiceDialog, setShowResetChoiceDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDeletedConfirmation, setShowDeletedConfirmation] = useState(false);
  const [deletionCancellationId, setDeletionCancellationId] = useState<string | null>(null);
  const [showCancelSubDialog, setShowCancelSubDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [showRegionPicker, setShowRegionPicker] = useState(false);
  const [showProviderPicker, setShowProviderPicker] = useState(false);
  const [currentRegion, setCurrentRegion] = useState(localStorage.getItem("ayeris_region") || "US");
  const [currentProviders, setCurrentProviders] = useState<number[]>(() => {
    try { return JSON.parse(localStorage.getItem("ayeris_providers") || "[]"); } catch { return []; }
  });
  const currentDeviceId = localStorage.getItem("ayeris_device_id");
  const { canPrompt, isInstalled, isIos, isStandalone, promptInstall } = usePwaInstall();
  const [showWaitlist, setShowWaitlist] = useState(false);

  const loadDevices = async () => {
    if (!user) return;
    const { data } = await supabase.from("user_devices")
      .select("*")
      .eq("user_id", user.id)
      .order("last_active", { ascending: false });
    setDevices(data || []);
  };

  useEffect(() => {
    if (user && showDevices) loadDevices();
  }, [user, showDevices]);

  const removeDevice = async (deviceDbId: string) => {
    await supabase.from("user_devices").delete().eq("id", deviceDbId);
    toast.success(t("settings.deviceRemoved"));
    loadDevices();
  };

  const handleUpgrade = () => {
    if (!user) { navigate("/auth"); return; }
    setShowPremiumModal(true);
  };

  const handleCancelSubscription = async () => {
    setActionLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("cancel-subscription");
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success(t("settings.cancelSubSuccess"));
      setShowCancelSubDialog(false);
      await checkSubscription();
    } catch (e: any) {
      toast.error(e.message || t("settings.cancelSubFailed"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetData = async () => {
    if (!user) return;
    setActionLoading(true);
    try {
      await Promise.all([
        supabase.from("user_bookmarks").delete().eq("user_id", user.id),
        supabase.from("user_watch_history").delete().eq("user_id", user.id),
        supabase.from("user_review_pending").delete().eq("user_id", user.id),
        supabase.from("user_favorites").delete().eq("user_id", user.id),
        supabase.from("user_hidden_titles").delete().eq("user_id", user.id),
        (supabase.from("user_genre_preferences") as any).delete().eq("user_id", user.id),
        (supabase.from("user_currently_watching") as any).delete().eq("user_id", user.id),
      ]);
      await setOnboarded(false);
      localStorage.removeItem("ayeris_favorites");
      localStorage.removeItem("ayeris_watch_history");
      localStorage.removeItem("ayeris_bookmarks");
      localStorage.removeItem("ayeris_hidden_titles");
      localStorage.removeItem("ayeris_starred");
      localStorage.removeItem("ayeris_review_pending");
      localStorage.removeItem("ayeris_review_migrated");
      localStorage.removeItem("ayeris_onboarded");
      localStorage.removeItem("ayeris_region");
      localStorage.removeItem("ayeris_providers");
      localStorage.removeItem("ayeris_db_migrated");
      localStorage.removeItem("ayeris_prefs_migrated");
      toast.success(t("settings.resetSuccess"));
      setShowResetDialog(false);
      setShowResetChoiceDialog(true);
    } catch (e: any) {
      toast.error(e.message || t("settings.resetFailed"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    setActionLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("delete-account");
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      const cancellationId = data?.cancellation_id || null;
      const keysToRemove = [
        "ayeris_onboarded", "ayeris_region", "ayeris_providers",
        "ayeris_favorites", "ayeris_watch_history", "ayeris_bookmarks",
        "ayeris_hidden_titles",
        "ayeris_starred", "ayeris_premium", "ayeris_premium_override",
        "ayeris_premium_type_override", "ayeris_device_id", "ayeris_db_migrated",
        "ayeris_prefs_migrated", "ayeris_dev_snapshot", "ayeris_language",
      ];
      keysToRemove.forEach(k => localStorage.removeItem(k));
      await signOut();
      setShowDeleteDialog(false);
      setDeleteConfirmText("");
      if (cancellationId) {
        setDeletionCancellationId(cancellationId);
        setShowDeletedConfirmation(true);
      } else {
        toast.success(t("settings.accountDeleted"));
        navigate("/landing", { replace: true });
      }
    } catch (e: any) {
      toast.error(e.message || t("settings.deleteFailed"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetOnboarding = async () => {
    localStorage.removeItem("ayeris_onboarded");
    localStorage.removeItem("ayeris_region");
    localStorage.removeItem("ayeris_providers");
    localStorage.removeItem("ayeris_favorites");
    localStorage.removeItem("ayeris_watch_history");
    localStorage.removeItem("ayeris_bookmarks");
    localStorage.removeItem("ayeris_hidden_titles");
    localStorage.removeItem("ayeris_starred");
    await setOnboarded(false);
    navigate("/onboarding");
  };

  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback((text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, []);

  const CopyableCode = ({ value }: { value: string }) => (
    <button
      onClick={() => handleCopy(value)}
      className="w-full flex items-center gap-2 p-3 rounded-lg bg-muted border border-border text-left group"
    >
      <code className="flex-1 text-xs font-mono text-foreground break-all select-all">{value}</code>
      {copied ? (
        <Check className="w-4 h-4 text-primary flex-shrink-0" />
      ) : (
        <Copy className="w-4 h-4 text-muted-foreground group-hover:text-foreground flex-shrink-0 transition-colors" />
      )}
    </button>
  );

  return (
    <div className="min-h-[100dvh] bg-background pb-16">
      <div className="px-4 pt-6 pb-6">
        <h1 className="font-display text-2xl font-bold text-foreground">{t("settings.title")}</h1>
      </div>

      <div className="px-4 space-y-3">
        {/* Profile Card */}
        {user ? (
          <div className="p-5 rounded-2xl bg-card border border-border space-y-4">
            <div className="flex flex-col items-center gap-2">
              <button onClick={() => setShowAvatarPicker(true)} className="relative group">
                <UserAvatar avatarUrl={avatarUrl} username={username} isPremium={isPremium} premiumType={premiumType} size="xl" />
                <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity flex items-center justify-center">
                  <Camera className="w-5 h-5 text-primary-foreground" />
                </div>
              </button>
              <button
                onClick={() => setShowAvatarPicker(true)}
                className="text-xs text-primary font-medium"
              >
                {t("settings.changeAvatar")}
              </button>
            </div>

            <div className="flex items-center justify-center gap-2">
              {showUsernameEdit ? null : (
                <>
                  <p className="text-base font-display font-bold text-foreground">
                    {username ? `@${username}` : t("settings.noUsername")}
                  </p>
                  <button
                    onClick={() => setShowUsernameEdit(true)}
                    className="p-1 rounded-md hover:bg-secondary transition-colors"
                    title={t("settings.editUsername")}
                  >
                    <AtSign className="w-3.5 h-3.5 text-muted-foreground" />
                  </button>
                </>
              )}
            </div>

            {showUsernameEdit && (
              <div className="p-3 rounded-xl bg-secondary/50 border border-border">
                <UsernameSetup
                  currentUsername={username}
                  onComplete={() => setShowUsernameEdit(false)}
                />
              </div>
            )}

            <div className="flex items-center justify-between pt-2 border-t border-border">
              <p className="text-xs text-muted-foreground truncate">{user.email}</p>
              <button
                onClick={async () => { await signOut(); navigate("/landing", { replace: true }); }}
                className="px-3 py-1.5 rounded-lg bg-secondary text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                {t("settings.signOut")}
              </button>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-border">
              <div>
                <p className="text-xs font-medium text-foreground">{t("settings.changePassword", "Change Password")}</p>
                <p className="text-[11px] text-muted-foreground">{t("settings.changePasswordDesc", "We'll send a reset link to your email")}</p>
              </div>
              <button
                disabled={changePasswordLoading}
                onClick={async () => {
                  if (!user?.email) return;
                  setChangePasswordLoading(true);
                  try {
                    const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
                      redirectTo: `${window.location.origin}/reset-password`,
                    });
                    if (error) throw error;
                    toast.success(t("resetPassword.emailSent", "Check your email for a password reset link."));
                  } catch (err: any) {
                    toast.error(err.message || t("common.error"));
                  } finally {
                    setChangePasswordLoading(false);
                  }
                }}
                className="px-3 py-1.5 rounded-lg bg-secondary text-xs font-medium text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
              >
                {changePasswordLoading ? t("common.sending", "Sending…") : t("settings.sendResetLink", "Send Link")}
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => navigate("/auth")}
            className="w-full flex items-center gap-3 p-4 rounded-xl bg-card border border-border"
          >
            <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center">
              <LogIn className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-foreground">{t("settings.signIn")}</p>
              <p className="text-xs text-muted-foreground">{t("settings.signInDesc")}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>
        )}

        {/* Premium */}
        <div className="p-4 rounded-xl bg-card border border-border space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-star/15 flex items-center justify-center">
              <Shield className="w-4 h-4 text-star" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">{t("settings.premium")}</p>
              <p className="text-xs text-muted-foreground">
                {isPremium
                  ? premiumType === "lifetime"
                    ? t("settings.premiumLifetime")
                    : t("settings.premiumMonthly")
                  : t("settings.premiumCta")}
              </p>
            </div>
            {!isPremium && (
              <button
                onClick={() => setShowPremiumModal(true)}
                className="px-3 py-1.5 rounded-lg bg-gradient-iris text-primary-foreground text-xs font-semibold disabled:opacity-50"
              >
                {t("settings.upgrade")}
              </button>
            )}
          </div>
          {isPremium && premiumType === "monthly" && (
            <div className="flex items-center justify-between pt-2 border-t border-border">
              <div>
                {subscriptionEnd && (
                  <p className="text-xs text-muted-foreground">
                    {cancelAtPeriodEnd
                      ? t("settings.cancelsOn", { date: new Date(subscriptionEnd).toLocaleDateString(i18n.language, { day: "numeric", month: "short", year: "numeric" }) })
                      : t("settings.renews", { date: new Date(subscriptionEnd).toLocaleDateString(i18n.language, { day: "numeric", month: "short", year: "numeric" }) })
                    }
                  </p>
                )}
              </div>
              {!cancelAtPeriodEnd && (
                <button
                  onClick={() => setShowCancelSubDialog(true)}
                  className="px-3 py-1.5 rounded-lg bg-destructive/10 text-destructive text-xs font-medium hover:bg-destructive/20 transition-colors"
                >
                  {t("settings.cancelSubscription")}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Manage Devices */}
        {user && (
          <>
            <button
              onClick={() => setShowDevices(!showDevices)}
              className="w-full flex items-center gap-3 p-4 rounded-xl bg-card border border-border"
            >
              <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center">
                <Smartphone className="w-4 h-4 text-primary" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-foreground">{t("settings.manageDevices")}</p>
                <p className="text-xs text-muted-foreground">{t("settings.maxDevices")}</p>
              </div>
              <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${showDevices ? "rotate-90" : ""}`} />
            </button>

            {showDevices && (
              <div className="space-y-2 ml-4">
                {devices.length === 0 && (
                  <p className="text-xs text-muted-foreground p-3">{t("settings.noDevices")}</p>
                )}
                {devices.map((d) => (
                  <div key={d.id} className="flex items-center gap-3 p-3 rounded-xl bg-secondary/50 border border-border">
                    <Smartphone className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-foreground">
                        {d.device_name || t("common.unknown", "Unknown")}
                        {d.device_id === currentDeviceId && (
                          <span className="ml-1.5 px-1.5 py-0.5 rounded bg-primary/15 text-primary text-[10px] font-semibold">{t("settings.thisDevice")}</span>
                        )}
                      </p>
                       <p className="text-[11px] text-muted-foreground">
                        {t("settings.lastActive")} {new Date(d.last_active).toLocaleDateString()} {new Date(d.last_active).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    {d.device_id !== currentDeviceId && (
                      <button
                        onClick={() => removeDevice(d.id)}
                        className="p-1.5 rounded-lg bg-destructive/10 hover:bg-destructive/20 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-destructive" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* User Preferences section */}
        <div className="pt-4 space-y-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">{t("settings.onboarding")}</p>

          {/* Language */}
          <div className="w-full flex items-center gap-3 p-4 rounded-xl bg-card border border-border">
            <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center">
              <Languages className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-foreground">{t("settings.language")}</p>
            </div>
            <select
              value={i18n.language?.split("-")[0] || "en"}
              onChange={(e) => { i18n.changeLanguage(e.target.value); if (user) { (supabase.from("profiles") as any).update({ language: e.target.value }).eq("id", user.id).then(() => {}); } }}
              className="bg-secondary border border-border rounded-lg px-2 py-1.5 text-xs text-foreground"
            >
              {Object.keys(TMDB_LANGUAGE_MAP).filter(k => ["en","es","fr","de","pt","ja","ko","zh","it","ru","tr","pl","nl","sv","da","fi","no","cs"].includes(k)).map(code => (
                <option key={code} value={code}>
                  {new Intl.DisplayNames([code], { type: "language" }).of(code)}
                </option>
              ))}
            </select>
          </div>

          {/* Region */}
          <button onClick={() => setShowRegionPicker(true)} className="w-full flex items-center gap-3 p-4 rounded-xl bg-card border border-border">
            <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center">
              <Globe className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-foreground">{t("settings.region")}</p>
              <p className="text-xs text-muted-foreground">{currentRegion}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>

          {/* Providers */}
          <button onClick={() => setShowProviderPicker(true)} className="w-full flex items-center gap-3 p-4 rounded-xl bg-card border border-border">
            <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center">
              <Tv className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-foreground">{t("settings.streamingServices")}</p>
              <p className="text-xs text-muted-foreground">{t("settings.providersSelected", { count: currentProviders.length })}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>


          {/* Re-enable Tutorials */}
          <div className="w-full flex items-center gap-3 p-4 rounded-xl bg-card border border-border">
            <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-foreground">{t("settings.tutorials", "Tutorials")}</p>
              <p className="text-xs text-muted-foreground">{t("settings.tutorialsDesc", "Re-enable discovery tutorials")}</p>
            </div>
            <button
              onClick={() => {
                localStorage.removeItem("ayeris_tutorial_dismissed_solo");
                localStorage.removeItem("ayeris_tutorial_dismissed_group");
                toast.success(t("settings.tutorialsReset", "Tutorials re-enabled for all session types"));
              }}
              className="px-3 py-1.5 rounded-lg bg-secondary text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              {t("settings.resetTutorials", "Reset")}
            </button>
          </div>
        </div>

        {/* Install App */}
        <div className="pt-4 space-y-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">{t("install.sectionTitle", "Install App")}</p>

          {!isStandalone && !isInstalled ? (
            <>
              <div className="p-4 rounded-xl bg-card border border-border space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center">
                    <Download className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">{t("install.addToHomeScreen", "Add to Home Screen")}</p>
                    <p className="text-xs text-muted-foreground">{t("install.settingsDesc", "Access Ayeris instantly — no download needed")}</p>
                  </div>
                </div>
                {canPrompt ? (
                  <button
                    onClick={() => promptInstall()}
                    className="w-full py-2.5 rounded-xl bg-gradient-iris text-primary-foreground font-display font-semibold text-sm shadow-iris active:scale-95 transition-transform flex items-center justify-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    {t("install.installNow", "Install Now")}
                  </button>
                ) : isIos ? (
                  <p className="text-xs text-muted-foreground p-3 rounded-xl bg-secondary/50 border border-border text-center">
                    {t("install.iosSettingsHint", "In Safari, tap the share button then \"Add to Home Screen\"")}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground p-3 rounded-xl bg-secondary/50 border border-border text-center">
                    {t("install.browserInstructions", "Use your browser menu to \"Add to Home Screen\" or \"Install App\"")}
                  </p>
                )}
              </div>
            </>
          ) : (
            <div className="p-4 rounded-xl bg-card border border-border">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center">
                  <Download className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">✅ {t("install.alreadyInstalled", "App Installed")}</p>
                  <p className="text-xs text-muted-foreground">{t("install.alreadyInstalledDesc", "You're running Ayeris from your home screen")}</p>
                </div>
              </div>
            </div>
          )}

          {/* Native app waitlist */}
          <button
            onClick={() => setShowWaitlist(true)}
            className="w-full flex items-center gap-3 p-4 rounded-xl bg-card border border-border"
          >
            <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center">
              <Smartphone className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-foreground">{t("waitlist.settingsTitle", "Native App Waitlist")}</p>
              <p className="text-xs text-muted-foreground">{t("waitlist.settingsDesc", "Register interest for iOS & Android apps")}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* Informational */}
        <div className="pt-4 space-y-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">{t("settings.informational", "Informational")}</p>

          <button
            onClick={() => navigate("/about")}
            className="w-full flex items-center gap-3 p-4 rounded-xl bg-card border border-border"
          >
            <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center">
              <Info className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-foreground">{t("settings.aboutAyeris", "About Ayeris")}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>

          <button
            onClick={() => navigate("/faq")}
            className="w-full flex items-center gap-3 p-4 rounded-xl bg-card border border-border"
          >
            <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center">
              <HelpCircle className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-foreground">{t("faq.title", "FAQ")}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>

          <button
            onClick={() => navigate("/guides")}
            className="w-full flex items-center gap-3 p-4 rounded-xl bg-card border border-border"
          >
            <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-foreground">{t("settings.guides", "Guides")}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>

          <button
            onClick={() => navigate("/blog")}
            className="w-full flex items-center gap-3 p-4 rounded-xl bg-card border border-border"
          >
            <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center">
              <PenLine className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-foreground">{t("settings.developerBlog", "Developer's Blog")}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* Legal & Important */}
        <div className="pt-4 space-y-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">{t("settings.legalImportant", "Legal & Important")}</p>

          <button
            onClick={() => navigate("/privacy-policy")}
            className="w-full flex items-center gap-3 p-4 rounded-xl bg-card border border-border"
          >
            <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center">
              <Shield className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-foreground">{t("settings.privacyPolicy", "Privacy Policy")}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>

          <button
            onClick={() => navigate("/terms-of-service")}
            className="w-full flex items-center gap-3 p-4 rounded-xl bg-card border border-border"
          >
            <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center">
              <FileText className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-foreground">{t("settings.termsOfService", "Terms of Service")}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* Danger Zone - only for signed-in users */}
        {user && (
          <div className="pt-4 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">{t("settings.dangerZone")}</p>

            {/* Reset Data */}
            <button
              onClick={() => setShowResetDialog(true)}
              className="w-full flex items-center gap-3 p-4 rounded-xl bg-card border border-border"
            >
              <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center">
                <Eraser className="w-4 h-4 text-amber-500" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-foreground">{t("settings.resetAccountData")}</p>
                <p className="text-xs text-muted-foreground">{t("settings.resetAccountDataDesc")}</p>
              </div>
            </button>

            {/* Delete Account */}
            <button
              onClick={() => setShowDeleteDialog(true)}
              className="w-full flex items-center gap-3 p-4 rounded-xl bg-card border border-destructive/30"
            >
              <div className="w-9 h-9 rounded-lg bg-destructive/15 flex items-center justify-center">
                <Trash2 className="w-4 h-4 text-destructive" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-destructive">{t("settings.deleteAccount")}</p>
                <p className="text-xs text-muted-foreground">{t("settings.deleteAccountDesc")}</p>
              </div>
            </button>
          </div>
        )}

        {/* Development - admin only */}
        {isAdmin && (
          <div className="pt-4 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">{t("settings.devTools")}</p>

            <button
              onClick={() => setShowDevTools(!showDevTools)}
              className="w-full flex items-center gap-3 p-4 rounded-xl bg-card border border-border"
            >
              <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center">
                <Bug className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-foreground">{t("settings.devTools")}</p>
                <p className="text-xs text-muted-foreground">{t("settings.devToolsDesc", "Testing options")}</p>
              </div>
              <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${showDevTools ? "rotate-90" : ""}`} />
            </button>

            {showDevTools && (
              <div className="ml-12 p-3 rounded-xl bg-secondary/50 border border-border space-y-3">
                <div>
                  <p className="text-xs font-medium text-foreground">{t("settings.premiumOverride")}</p>
                  <p className="text-[11px] text-muted-foreground mb-2">{t("settings.premiumOverrideDesc", "Simulate premium tiers")}</p>
                  <div className="flex gap-1.5">
                    {([
                      { label: t("settings.overrideOff", "Off"), val: false as const, type: null },
                      { label: t("settings.overrideMonthly", "Monthly"), val: true as const, type: "monthly" },
                      { label: t("settings.overrideLifetime", "Lifetime"), val: true as const, type: "lifetime" },
                    ] as const).map((opt) => {
                      const isActive = !isPremium && !opt.val
                        ? true
                        : isPremium && premiumType === opt.type;
                      return (
                        <button
                          key={opt.label}
                          onClick={() => setPremiumOverride(opt.val ? true : null, opt.val ? opt.type : null)}
                          className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                            isActive
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted text-muted-foreground hover:bg-muted/80"
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="border-t border-border pt-3 space-y-2">
                  <p className="text-xs font-medium text-foreground">{t("settings.onboardingSnapshot", "Onboarding Snapshot")}</p>
                  <p className="text-[11px] text-muted-foreground">{t("settings.onboardingSnapshotDesc", "Save your current region, providers & favorites so you can quickly restore them after testing resets.")}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        const snapshot = {
                          region: localStorage.getItem("ayeris_region"),
                          providers: localStorage.getItem("ayeris_providers"),
                          favorites: localStorage.getItem("ayeris_favorites"),
                          onboarded: localStorage.getItem("ayeris_onboarded"),
                        };
                        localStorage.setItem("ayeris_dev_snapshot", JSON.stringify(snapshot));
                        toast.success(t("settings.snapshotSaved", "Snapshot saved"));
                      }}
                       className="flex-1 py-1.5 rounded-lg bg-primary/15 text-primary text-xs font-semibold active:scale-95 transition-transform"
                    >
                      💾 {t("common.save")}
                    </button>
                    <button
                      onClick={() => {
                        const raw = localStorage.getItem("ayeris_dev_snapshot");
                        if (!raw) { toast.error(t("settings.noSnapshot", "No snapshot saved yet")); return; }
                        const snap = JSON.parse(raw);
                        if (snap.region) localStorage.setItem("ayeris_region", snap.region);
                        if (snap.providers) localStorage.setItem("ayeris_providers", snap.providers);
                        if (snap.favorites) localStorage.setItem("ayeris_favorites", snap.favorites);
                        if (snap.onboarded) localStorage.setItem("ayeris_onboarded", snap.onboarded);
                        setCurrentRegion(snap.region || "US");
                        try { setCurrentProviders(JSON.parse(snap.providers || "[]")); } catch {}
                        toast.success(t("settings.snapshotRestored", "Snapshot restored — reload or navigate to apply"));
                      }}
                      className="flex-1 py-1.5 rounded-lg bg-primary/15 text-primary text-[11px] font-semibold active:scale-95 transition-transform"
                    >
                      ♻️ {t("settings.restore", "Restore")}
                    </button>
                  </div>
                  {localStorage.getItem("ayeris_dev_snapshot") && (
                    <p className="text-[10px] text-muted-foreground">✅ {t("settings.snapshotExists", "Snapshot exists")}</p>
                  )}
                </div>
              </div>
            )}

            {/* Reset Onboarding */}
            <button
              onClick={handleResetOnboarding}
              className="w-full flex items-center gap-3 p-4 rounded-xl bg-card border border-border"
            >
              <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center">
                <RotateCcw className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-foreground">{t("settings.redoOnboarding")}</p>
                <p className="text-xs text-muted-foreground">{t("settings.startFresh")}</p>
              </div>
            </button>
          </div>
        )}
      </div>

      {/* Reset Data Dialog */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent className="max-w-sm mx-auto rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Eraser className="w-5 h-5 text-amber-500" />
              {t("settings.resetAccountData")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("settings.resetConfirmDesc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetData}
              disabled={actionLoading}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {actionLoading ? t("settings.resetting") : t("settings.resetAllData")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Subscription Dialog */}
      <AlertDialog open={showCancelSubDialog} onOpenChange={setShowCancelSubDialog}>
        <AlertDialogContent className="max-w-sm mx-auto rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              {t("settings.cancelSubTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">{t("settings.cancelSubIntro")}</span>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>{t("settings.cancelSubLoseAds")}</li>
                <li>{t("settings.cancelSubAdsReturn")}</li>
                <li>{t("settings.cancelSubGroupRevert")}</li>
              </ul>
              {subscriptionEnd && (
                <span className="block p-2.5 rounded-lg bg-muted border border-border text-xs font-medium text-foreground">
                  {t("settings.cancelSubKeepUntil", { date: new Date(subscriptionEnd).toLocaleDateString(i18n.language, { day: "numeric", month: "short", year: "numeric" }) })}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>{t("settings.keepSubscription")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelSubscription}
              disabled={actionLoading}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              {actionLoading ? t("settings.cancelling") : t("settings.yesCancel")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Choice Dialog (after successful reset) */}
      <AlertDialog open={showResetChoiceDialog} onOpenChange={setShowResetChoiceDialog}>
        <AlertDialogContent className="max-w-sm mx-auto rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              ✅ {t("settings.resetChoiceTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("settings.resetChoiceDesc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
            <AlertDialogAction
              onClick={() => {
                setShowResetChoiceDialog(false);
                navigate("/onboarding");
              }}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              {t("settings.redoOnboardingBtn")}
            </AlertDialogAction>
            <AlertDialogAction
              onClick={async () => {
                setShowResetChoiceDialog(false);
                await signOut();
                navigate("/landing", { replace: true });
              }}
              className="w-full bg-secondary hover:bg-secondary/80 text-foreground"
            >
              {t("settings.logOut")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Account Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={(open) => { setShowDeleteDialog(open); if (!open) setDeleteConfirmText(""); }}>
        <AlertDialogContent className="max-w-sm mx-auto rounded-2xl max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              {t("settings.deleteAccountPermanently")}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>{t("settings.deleteWarning")}</p>

                {isPremium && premiumType === "monthly" && (
                  <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 space-y-1">
                    <p className="text-xs font-semibold text-destructive">{t("settings.monthlySubLabel")}</p>
                    <p className="text-xs text-destructive">{t("settings.monthlySubWarning")}</p>
                  </div>
                )}

                {isPremium && premiumType === "lifetime" && (
                  <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 space-y-1">
                    <p className="text-xs font-semibold text-destructive">{t("settings.lifetimeLabel")}</p>
                    <p className="text-xs text-destructive">{t("settings.lifetimeWarning")}</p>
                  </div>
                )}

                <div className="p-3 rounded-lg bg-muted border border-border space-y-1.5">
                  <p className="text-xs font-semibold text-foreground">{t("settings.whatWillBeDeleted")}</p>
                  <ul className="list-disc list-inside space-y-0.5 text-xs">
                    <li>{t("settings.deleteItem1")}</li>
                    <li>{t("settings.deleteItem2")}</li>
                    <li>{t("settings.deleteItem3")}</li>
                    <li>{t("settings.deleteItem4")}</li>
                    <li>{t("settings.deleteItem5")}</li>
                  </ul>
                </div>

                <div className="p-3 rounded-lg bg-muted border border-border space-y-1">
                  <p className="text-xs font-semibold text-foreground">{t("settings.afterDeletion")}</p>
                  <ul className="list-disc list-inside space-y-0.5 text-xs">
                    <li>{t("settings.afterDelete1")}</li>
                    <li>{t("settings.afterDelete2")}</li>
                    <li>{t("settings.afterDelete3")}</li>
                  </ul>
                </div>

                {isPremium && premiumType === "monthly" && (
                  <p className="text-xs text-muted-foreground">
                    {t("settings.cancelConfirmEmail")}
                  </p>
                )}

                <p className="text-xs">{t("settings.typeDelete")}</p>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={t("settings.typeDeletePlaceholder", "Type DELETE")}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-destructive/50"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={actionLoading || deleteConfirmText !== "DELETE"}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground disabled:opacity-40"
            >
              {actionLoading ? t("settings.deleting") : t("settings.deleteForever")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Deletion Confirmation with Cancellation ID */}
      <AlertDialog open={showDeletedConfirmation} onOpenChange={(open) => {
        if (!open) {
          setShowDeletedConfirmation(false);
          navigate("/landing", { replace: true });
        }
      }}>
        <AlertDialogContent className="max-w-sm mx-auto rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              ✅ {t("settings.deletedTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>{t("settings.deletedDesc")}</p>
                <CopyableCode value={deletionCancellationId || ""} />
                <p className="text-xs">{t("settings.deletedFreeToCreate")}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => {
                setShowDeletedConfirmation(false);
                navigate("/landing", { replace: true });
              }}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              {t("common.done")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Region Picker */}
      <RegionPicker
        open={showRegionPicker}
        onOpenChange={setShowRegionPicker}
        currentRegion={currentRegion}
        onSelect={(code) => {
          setCurrentRegion(code);
          saveRegion(code);
          toast.success(t("settings.regionUpdated", { region: code }));
        }}
      />

      {/* Provider Picker */}
      <ProviderPicker
        open={showProviderPicker}
        onOpenChange={setShowProviderPicker}
        region={currentRegion}
        selectedIds={currentProviders}
        onSave={(ids) => {
          setCurrentProviders(ids);
          saveProviders(ids);
          toast.success(t("settings.providersSaved", { count: ids.length }));
        }}
      />
      <SocialLinks className="mt-4" />
      <p className="text-[10px] text-muted-foreground/60 text-center py-4">© 2026 Ayeris.tv — All rights reserved.</p>
      <GoPremiumModal open={showPremiumModal} onClose={() => setShowPremiumModal(false)} />
      <NativeAppWaitlistModal open={showWaitlist} onClose={() => setShowWaitlist(false)} />
      <AvatarPicker
        open={showAvatarPicker}
        onClose={() => setShowAvatarPicker(false)}
        currentAvatarUrl={avatarUrl}
        onAvatarChange={() => {}}
      />
    </div>
  );
};

export default Settings;
